<script setup lang="ts">
import Icon from '~/components/global/Icon.vue'
import type { useFilterState } from '~/composables/filter/useFilterState'
import type { EstateType } from '~/types/EstateType'
import trackGtmEvent from '~/utils/tracking/tackGtmEvent'

const props = defineProps<{
  filterState: ReturnType<typeof useFilterState>
  estateType: EstateType
  showResults?: () => void
}>()

const searchTerm = ref(props.filterState?.filterValues?.value.search || '')

watch(
  () => props.filterState.filterValues.value.search,
  (newVal) => {
    if (newVal === searchTerm.value) return

    searchTerm.value = newVal
  }
)

watch(searchTerm, (newVal) => {
  if (newVal) {
    props.filterState.actions.updateValue('search', newVal)
  } else {
    props.filterState.actions.deleteValue('search')
  }

  trackSearchEventOffset()

  props.filterState.actions.submitFilter()
})

function onInputBlur() {
  trackSearchEventOffset(0)
}

let lastTrackedSearchTerm: string | undefined
let searchEventTimeout: ReturnType<typeof setTimeout> | null = null
function trackSearchEventOffset(timeout = 2500) {
  if (searchEventTimeout) {
    clearTimeout(searchEventTimeout)
  }

  searchEventTimeout = setTimeout(() => {
    if (!searchTerm.value) return
    if (searchTerm.value === lastTrackedSearchTerm) return

    const pageTypes = {
      office: 'listing-kancelarske-priestory',
      warehouse: 'listing-sklady-haly-a-pozemky',
      retail: 'listing-obchodne-priestory',
    }

    trackGtmEvent('vyhladavanie', {
      search_term: searchTerm.value,
      page_type: pageTypes[props.estateType],
    })

    lastTrackedSearchTerm = searchTerm.value
  }, timeout)
}
</script>

<template>
  <div class="c-filter-search">
    <IInput
      v-model="searchTerm"
      type="search"
      name="searchKeyword"
      :placeholder="$t('filter.search.inputPlaceholder')"
      @keydown.enter="props.showResults"
      @blur="onInputBlur"
    >
      <template #suffix>
        <Icon name="search" />
      </template>
    </IInput>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-filter-search {
  :deep(.input) {
    position: relative;
    align-items: stretch;
    color: var(--color-primary);
    border: none;

    & > input {
      padding-right: 50px;
    }

    .input-suffix {
      border: none;
      position: absolute;
      right: 0;
      top: 0;
      height: 100%;
      font-size: 18px;
      color: var(--color-primary);
      pointer-events: none;
    }
  }
}
</style>
~/utils/tracking/tackGtmEvent
